var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAppSelector } from "@/hooks/store";
import WidgetDisplay from "@/components/widgets/widgetDisplay";
import { Slot } from "@/utils/types/layout";
import { ScrollContainer } from "./scrollContainer";
import { memo, useEffect, useMemo, useState, useRef } from "react";
// Lazy Widget Component with smarter loading
var LazyWidget = memo(function (_a) {
    var widget = _a.widget, index = _a.index, shouldLoadImmediately = _a.shouldLoadImmediately;
    var _b = useState(shouldLoadImmediately), isVisible = _b[0], setIsVisible = _b[1];
    var _c = useState(shouldLoadImmediately), hasBeenLoaded = _c[0], setHasBeenLoaded = _c[1];
    var ref = useRef(null);
    useEffect(function () {
        if (shouldLoadImmediately)
            return; // Skip intersection observer for immediate widgets
        var observer = new IntersectionObserver(function (_a) {
            var entry = _a[0];
            if (entry.isIntersecting && !hasBeenLoaded) {
                setIsVisible(true);
                setHasBeenLoaded(true);
                observer.disconnect();
            }
        }, {
            rootMargin: "100px",
            threshold: 0.1,
        });
        if (ref.current) {
            observer.observe(ref.current);
        }
        return function () { return observer.disconnect(); };
    }, [shouldLoadImmediately, hasBeenLoaded]);
    // Don't show loading skeleton for off-screen widgets
    if (!isVisible && !shouldLoadImmediately) {
        return _jsx("div", { ref: ref, style: { minHeight: "150px" } }); // Invisible placeholder
    }
    return (_jsx("div", __assign({ ref: ref }, { children: isVisible ? (_jsx(WidgetDisplay, { widget: widget }, widget.id)) : (_jsxs("div", __assign({ className: "flex items-center justify-center h-24 bg-gray-100 rounded animate-pulse" }, { children: ["Loading widget ", index + 1, "..."] }))) })));
});
function Main() {
    var _a = useAppSelector(function (state) { return ({
        widgets: state.layout.slots[Slot.MAIN].widgets,
        isEditingLayout: state.layout.editingLayout,
    }); }), widgets = _a.widgets, isEditingLayout = _a.isEditingLayout;
    var isHome = useMemo(function () {
        return widgets.some(function (w) { return w.type === "widget/home/dashboard"; });
    }, [widgets]);
    var bgImage = useAppSelector(function (state) { return state.theme; }).bgImage;
    var haveImgBg = !!(bgImage === null || bgImage === void 0 ? void 0 : bgImage.file);
    var visibleWidgets = useMemo(function () { return widgets.filter(function (w) { var _a; return (_a = w.meta) === null || _a === void 0 ? void 0 : _a.visible; }); }, [widgets]);
    // Load first 2-3 widgets immediately, rest lazily
    var IMMEDIATE_LOAD_COUNT = 2;
    var widgetsContent = visibleWidgets.map(function (widget, index) { return (_jsx(LazyWidget, { widget: widget, index: index, shouldLoadImmediately: index < IMMEDIATE_LOAD_COUNT }, widget.id)); });
    var bgClasses = isHome
        ? haveImgBg
            ? ""
            : "bg-[color:var(--colors-main-bg)] dark:bg-[var(--colors-bg-default)]"
        : "";
    var mainClasses = "flex flex-col ".concat(isEditingLayout
        ? "flex-1 min-h-0 overflow-y-hidden"
        : "flex-auto overflow-auto", " ").concat(bgClasses);
    var mainContent = _jsx("main", __assign({ className: mainClasses }, { children: widgetsContent }));
    return isEditingLayout ? (mainContent) : (_jsx(ScrollContainer, __assign({ className: "relative h-full widgets" }, { children: mainContent })));
}
export default memo(Main);
